import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import {
  ArrowDownToLine,
  ArrowUpToLine,
  GripVertical,
  ListOrdered,
  MoreHorizontal,
} from 'lucide-react';
import { memo, useImperativeHandle, useRef, useState, type ReactNode, type Ref } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/shared/components/status-badge';
import { cn } from '@/shared/lib/utils';

import { moveItem, orderRowId, type Identified } from '@/shared/lib/order-list';

/** Every row has the same height, so the virtualizer never has to measure. */
const ROW_HEIGHT = 40;

export interface SortableOrderListHandle {
  /** Scrolls an item into view, even when its row is not rendered yet. */
  scrollToItem: (id: number) => void;
}

/**
 * An ordered list the admin rearranges: drag a row by its handle (mouse or
 * touch), or with the keyboard (focus the handle, Space to pick up, arrow keys
 * to move, Space to drop, Escape to cancel), or use a row's menu to move it to
 * the top, the bottom or a given position.
 *
 * The list is shown exactly in the order it is given — it never sorts. Each row
 * shows its resulting position, and a "Moved" marker when that differs from
 * where it was when the editor opened (`originalIndex`).
 *
 * Built for hundreds of items: the whole list stays in memory (a save always
 * sends every id), but only the rows in view are rendered (virtualized), and a
 * row's content is memoized, so a drag re-renders a few small handles per frame
 * instead of every row. The row being dragged is always kept rendered.
 */
export function SortableOrderList<T extends Identified>({
  ref,
  listId,
  label,
  items,
  onChange,
  originalIndex,
  itemLabel,
  renderItem,
  highlighted,
  disabled = false,
  className,
}: {
  ref?: Ref<SortableOrderListHandle>;
  listId: string;
  /** Accessible name of the list, e.g. "Storefront brand order". */
  label: string;
  items: T[];
  onChange: (items: T[]) => void;
  originalIndex: ReadonlyMap<number, number>;
  itemLabel: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  /** Rows to emphasise (search or category matches). Never hides anything. */
  highlighted?: ReadonlySet<number>;
  disabled?: boolean;
  /** Sizes the scroll area, e.g. `min-h-0 flex-1`. */
  className?: string;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [movingTo, setMovingTo] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const indexOf = (id: UniqueIdentifier | null | undefined) =>
    id == null ? -1 : items.findIndex((item) => item.id === id);
  const activeIndex = indexOf(activeId);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    getItemKey: (index) => items[index]?.id ?? index,
    overscan: 10,
    // The first paint, before the scroll area is measured, already shows rows.
    initialRect: { width: 0, height: 15 * ROW_HEIGHT },
    // Keep the dragged row mounted while it is carried out of view.
    rangeExtractor: (range) => {
      const indexes = defaultRangeExtractor(range);
      if (activeIndex < 0 || indexes.includes(activeIndex)) return indexes;
      const at = indexes.findIndex((index) => index > activeIndex);
      indexes.splice(at < 0 ? indexes.length : at, 0, activeIndex);
      return indexes;
    },
  });

  useImperativeHandle(ref, () => ({
    scrollToItem: (id) => {
      const index = indexOf(id);
      if (index >= 0) virtualizer.scrollToIndex(index, { align: 'center' });
    },
  }));

  const nameOf = (id: UniqueIdentifier) => {
    const item = items[indexOf(id)];
    return item ? itemLabel(item) : String(id);
  };
  const positionOf = (id: UniqueIdentifier | undefined) => indexOf(id) + 1;

  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      `Picked up ${nameOf(active.id)}, position ${positionOf(active.id)} of ${items.length}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${nameOf(active.id)} is over position ${positionOf(over.id)} of ${items.length}.`
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? `${nameOf(active.id)} dropped at position ${positionOf(over.id)} of ${items.length}.`
        : `${nameOf(active.id)} dropped.`,
    onDragCancel: ({ active }) => `Moving ${nameOf(active.id)} was cancelled.`,
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    onChange(moveItem(items, Number(active.id), positionOf(over.id) - 1));
  };

  const moveTo = (id: number, position: number) => onChange(moveItem(items, id, position - 1));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => setActiveId(Number(active.id))}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable:
            'To move an item, press Space or Enter on its handle. Use the arrow keys to move it, Space or Enter to drop it, Escape to cancel.',
        },
      }}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <div ref={scrollRef} className={cn('overflow-y-auto rounded-md border', className)}>
          <ol
            aria-label={label}
            className="relative"
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualizer.getVirtualItems().map((row) => {
              const item = items[row.index];
              if (!item) return null;
              return (
                <SortableRow
                  key={item.id}
                  rowId={orderRowId(listId, item.id)}
                  id={item.id}
                  start={row.start}
                  position={row.index + 1}
                  count={items.length}
                  name={itemLabel(item)}
                  moved={originalIndex.get(item.id) !== row.index}
                  highlighted={highlighted?.has(item.id) ?? false}
                  disabled={disabled}
                  movingTo={movingTo === item.id}
                  onStartMoveTo={() => setMovingTo(item.id)}
                  onEndMoveTo={() => setMovingTo(null)}
                  onMoveTo={(position) => moveTo(item.id, position)}
                >
                  {renderItem(item)}
                </SortableRow>
              );
            })}
          </ol>
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface RowProps {
  name: string;
  position: number;
  count: number;
  moved: boolean;
  disabled: boolean;
  movingTo: boolean;
  onStartMoveTo: () => void;
  onEndMoveTo: () => void;
  onMoveTo: (position: number) => void;
  children: ReactNode;
}

/**
 * The part of a row that re-renders on every drag frame: only the sortable
 * wrapper and its handle. Everything else is in the memoized `RowBody`.
 */
function SortableRow({
  rowId,
  id,
  start,
  highlighted,
  ...body
}: RowProps & { rowId: string; id: number; start: number; highlighted: boolean }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: body.disabled });

  return (
    <li
      id={rowId}
      ref={setNodeRef}
      aria-posinset={body.position}
      aria-setsize={body.count}
      style={{
        height: ROW_HEIGHT,
        // The virtual position goes in `top`, not `transform`: dnd-kit measures
        // rows with their transform removed, and uses it for the sorting offset.
        top: start,
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      data-moved={body.moved || undefined}
      data-highlighted={highlighted || undefined}
      className={cn(
        'bg-background absolute inset-x-0 flex items-center gap-2 px-2 text-xs',
        body.position < body.count && 'border-border border-b',
        highlighted && 'bg-primary/5 ring-primary/40 ring-1 ring-inset',
        isDragging && 'z-10 shadow-md',
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        disabled={body.disabled}
        aria-label={`Move ${body.name}, position ${body.position} of ${body.count}`}
        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -ms-1 flex size-7 cursor-grab touch-none items-center justify-center rounded-md focus-visible:ring-2 focus-visible:outline-none active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
      <RowBody {...body} />
    </li>
  );
}

/** Skipped on drag frames: its props do not change while an item is carried. */
const RowBody = memo(function RowBody({
  name,
  position,
  count,
  moved,
  disabled,
  movingTo,
  onStartMoveTo,
  onEndMoveTo,
  onMoveTo,
  children,
}: RowProps) {
  return (
    <>
      <span className="text-muted-foreground w-8 shrink-0 text-end tabular-nums" aria-hidden>
        {position}
      </span>
      <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
      {moved && <StatusBadge variant="brand">Moved</StatusBadge>}
      {movingTo ? (
        <MoveToPosition
          name={name}
          current={position}
          count={count}
          onMove={(target) => {
            onMoveTo(target);
            onEndMoveTo();
          }}
          onCancel={onEndMoveTo}
        />
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label={`Move options for ${name}`}
            >
              <MoreHorizontal className="size-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={position === 1} onSelect={() => onMoveTo(1)}>
              <ArrowUpToLine aria-hidden />
              Move to top
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onStartMoveTo}>
              <ListOrdered aria-hidden />
              Move to position…
            </DropdownMenuItem>
            <DropdownMenuItem disabled={position === count} onSelect={() => onMoveTo(count)}>
              <ArrowDownToLine aria-hidden />
              Move to bottom
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
});

function MoveToPosition({
  name,
  current,
  count,
  onMove,
  onCancel,
}: {
  name: string;
  current: number;
  count: number;
  onMove: (position: number) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(String(current));
  const target = Number(value);
  const valid = Number.isInteger(target) && target >= 1 && target <= count;

  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) onMove(target);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <Input
        autoFocus
        type="number"
        min={1}
        max={count}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        aria-label={`New position for ${name}, 1 to ${count}`}
        aria-invalid={!valid}
        className="h-7 w-20 text-xs"
      />
      <Button type="submit" size="sm" className="h-7" disabled={!valid}>
        Move
      </Button>
      <Button type="button" size="sm" variant="ghost" className="h-7" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}
